import { useState } from "react";
import { Check, ChevronDown, ChevronRight, Loader, Square, Terminal, X } from "lucide-react";
import type { CommandView } from "@racler/protocol";
import { commandLine, commandOk, commandStatusText, policyLabel } from "../lib/commandView.ts";

interface Props {
  c: CommandView;
  onApprove: (c: CommandView) => Promise<void>;
  onDeny: (c: CommandView) => Promise<void>;
  onCancel: (c: CommandView) => Promise<void>;
}

/**
 * A command the agent requested. Auto-allowed observational commands are compact; approval commands are review
 * cards. Approving means "run THIS exact command" (the browser sends the frozen hash shown here). Output is plain text
 * (untrusted) and collapsed by default.
 */
export function CommandCard({ c, onApprove, onDeny, onCancel }: Props) {
  const [showOutput, setShowOutput] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = c.status === "pending_approval";
  const running = c.status === "running";
  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const Icon = running
    ? Loader
    : commandOk(c)
      ? Check
      : pending
        ? Terminal
        : c.status === "completed"
          ? X
          : X;
  const hasOutput = !!c.result && (c.result.stdout !== "" || c.result.stderr !== "");
  const compact = c.policy === "allow" && !pending;

  return (
    <div
      className={`command-card cmd-${c.status}${compact ? " compact" : ""}`}
      data-testid="command-card"
      data-status={c.status}
      data-id={c.id}
    >
      <div className="command-head">
        <Icon size={13} aria-hidden className={running ? "spin" : ""} />
        <code className="command-line" data-testid="command-line">
          {commandLine(c)}
        </code>
      </div>
      {!compact && (
        <dl className="command-meta">
          <dt>Working directory</dt>
          <dd>
            <code>{c.cwd}</code>
          </dd>
          {c.reason && (
            <>
              <dt>Reason</dt>
              <dd>{c.reason}</dd>
            </>
          )}
          <dt>Policy</dt>
          <dd data-testid="command-policy">
            {policyLabel(c)} — {c.policyReason}
          </dd>
        </dl>
      )}
      <div className="command-status muted small" data-testid="command-status">
        {commandStatusText(c)}
      </div>
      {c.changedFiles.length > 0 && (
        <div className="command-changed" data-testid="command-changed">
          Changed files (not reviewed as a ChangeSet): {c.changedFiles.join(", ")}
          {c.changedFilesTruncated ? " …" : ""}
        </div>
      )}
      {pending && (
        <div className="command-actions">
          <button
            className="primary"
            disabled={busy}
            onClick={() => void act(() => onApprove(c))}
            data-testid="command-approve"
          >
            Allow once
          </button>
          <button disabled={busy} onClick={() => void act(() => onDeny(c))} data-testid="command-deny">
            Deny
          </button>
        </div>
      )}
      {running && (
        <div className="command-actions">
          <button disabled={busy} onClick={() => void act(() => onCancel(c))} data-testid="command-stop">
            <Square size={11} fill="currentColor" aria-hidden /> Stop
          </button>
        </div>
      )}
      {hasOutput && (
        <button
          className="tools-toggle"
          onClick={() => setShowOutput((v) => !v)}
          data-testid="command-output-toggle"
        >
          {showOutput ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {showOutput ? "Hide output" : "Show output"}
          {c.result?.stdoutTruncated || c.result?.stderrTruncated ? " (truncated)" : ""}
        </button>
      )}
      {showOutput && c.result && (
        <pre className="command-output" data-testid="command-output">
          {c.result.stdout}
          {c.result.stderr && `\n--- stderr ---\n${c.result.stderr}`}
        </pre>
      )}
      {error && (
        <div className="msg-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
